import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Inject,
  OnDestroy,
  OnInit,
  Optional,
} from '@angular/core';
import { catchError, forkJoin, of, Subject, takeUntil } from 'rxjs';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import * as _ from 'lodash-es';

import { AppService } from '../../../../../app.service';
import { GlobalService } from '../../../../../global/services/global.service';
import { GtwSnackbarService } from '../../../../../shared/_services/gtw-snackbar.service';
import { MasterDataService } from '../../master-data.service';
import {
  findAppkitItemByValue,
  getAppkitDropdownSelection,
  mapToAppkitDropdownItems,
  setDropdownAriaLabel
} from '../../../../../shared/utils/appkit-dropdown.util';

interface FormLineMapping {
  form: any;
  schedule: any;
  line: any;
  column: any;
  section: any;
  part: any;
  form_chart_key: any;
  form_seq_key: any;
}

interface ClientItem {
  id: any;
  label: string;
  is_active: 'A' | 'N';
}

/**
 * Replicates AngularJS `CreateSytemAccountCtrl` + `create-system-account.html`
 * (system-mapping/create-system-account-modal — "Create new System Account").
 *
 * Sections:
 *  1. Account / Account Description / Account Type / Source System / System Chart
 *  2. "Create from" dropdown:
 *     - fromExistingSysAcct  → single dropdown of sys accounts (action_code 0q4gwm)
 *     - fromNewFormMapping   → cascading rows of form/schedule/line/column/section/part + form_chart_key
 *  3. isClientSpecific checkbox → multi-select of non-default clients.
 *
 * Save (`MasterDataService.createSysAccount`) builds:
 *   { account, account_desc, account_type, source_sys, sys_chart_key,
 *     is_client_specific, starter_ds, fromSysAcctKey, form_data,
 *     selectedClients, selectedClientsString }
 */
@Component({
    selector: 'gtw-create-system-account-modal',
    templateUrl: './create-system-account-modal.component.html',
    styleUrls: ['./create-system-account-modal.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CreateSystemAccountModalComponent implements OnInit, OnDestroy {
  loading = true;
  crudLoading = false;
  submitted = false;

  // Form fields
  newSysAccount = '';
  newSysAccountDesc = '';
  acctType: any = null;             // {ACCT_TYPE: ...}
  sourceSystem: any = null;          // {SOURCE_SYSTEM: ...}
  selectedSysChart: any = null;      // SYS_CHART_KEY (number)

  // "Create from" mode
  createFrom: '' | 'fromExistingSysAcct' | 'fromNewFormMapping' = '';
  selectedFromSystemAcct: any = null;

  /** Static options for the "Create from" p-dropdown. Mirrors the original AngularJS <select>. */
  createFromOptions = [
    { value: 'fromExistingSysAcct', label: 'Create from existing' },
    { value: 'fromNewFormMapping', label: 'Create from form mapping' },
  ];

  // Lookups
  accountTypes: any[] = [];
  sourceSystemList: any[] = [];
  sysChartList: any[] = [];
  formChartList: any[] = [];
  sys_acc_dropdown_data: any[] = [];
  loadSystemAcctLookupTemp: any[] = [];

  loadLookupForm: any[] = [];

  // Per-row cascading lookups (mirrors `loadLookupSchedule[$index]` etc.)
  scheduleLookup: any[][] = [];
  lineLookup: any[][] = [];
  columnLookup: any[][] = [];
  sectionLookup: any[][] = [];
  partLookup: any[][] = [];

  formLineMapping: FormLineMapping[] = [];

  // Client selection
  isClientSpecific = false;
  clientList: ClientItem[] = [];
  clientDropdownList: ClientItem[] = [];
  selectedClientList: ClientItem[] = [];
  starter_ds = '';

  private rowData: any = {};
  private taxYear: any = '';
  private scenario: any = '';

  private readonly destroy$ = new Subject<void>();

  constructor(
    @Optional() public dialogRef: MatDialogRef<CreateSystemAccountModalComponent>,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any,
    private masterDataService: MasterDataService,
    private appService: AppService,
    private globalService: GlobalService,
    private snackbar: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) {
    this.rowData = data?.rowData || {};
    this.taxYear = data?.taxYear ?? this.globalService.global?.globalParams?.tax_year ?? '';
    this.scenario = data?.scenario ?? this.globalService.global?.globalParams?.scenario ?? '';
  }

  ngOnInit(): void {
    this.formLineMapping = [this.newFormLine()];
    this.buildClientList();
    this.loadAllLookups();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ----------------------------------------------------------------
  // Lookup loaders
  // ----------------------------------------------------------------

  private loadAllLookups(): void {
    this.loading = true;
    this.cdr.detectChanges();

    const safe = <T>(actionCode: string, extra: any = {}) =>
      this.masterDataService.loadJsonObject(actionCode, { tax_year: this.taxYear, ...extra })
        .pipe(catchError(() => of({ callSuccess: '0', jsonObject: [] as any[] })));

    forkJoin({
      acctTypes: safe('ve7098'),
      sysAcctDropdown: safe('0q4gwm'),
      sourceSystems: safe('umsehd'),
      sysChart: safe('yw3l4h'),
      formChart: safe('wkl7ky'),
      lookupTemp: safe('sv6aun'),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: any) => {
        this.accountTypes = res?.acctTypes?.jsonObject || [];
        this.sys_acc_dropdown_data = res?.sysAcctDropdown?.jsonObject || [];
        this.sourceSystemList = res?.sourceSystems?.jsonObject || [];
        this.sysChartList = res?.sysChart?.jsonObject || [];
        this.formChartList = res?.formChart?.jsonObject || [];

        const lookupTemp: any[] = res?.lookupTemp?.jsonObject || [];
        this.loadSystemAcctLookupTemp = lookupTemp;
        this.loadLookupForm = _.uniq(lookupTemp.map((o: any) => o?.FORM));
        this.refreshTopDropdownItems();

        this.loading = false;
        this.cdr.detectChanges();
      });
  }

  // ----------------------------------------------------------------
  // Client list
  // ----------------------------------------------------------------

  private buildClientList(): void {
    const clients = this.appService.USER_SETTINGS?.user?.clients || [];
    clients.forEach((c: any) => {
      if (c.client_key !== 0) {
        this.clientList.push({
          id: c.client_datasource,
          label: c.client_name,
          is_active: 'N',
        });
      } else {
        // Replicates AngularJS: starter_ds comes from the default (client_key === 0) entry.
        this.starter_ds = c.client_datasource;
      }
    });
    this.clientList.forEach((client: any) => {
      client.showCheckbox = true;
      client.role = 'checkbox';
    });
    this.clientDropdownList = [...this.clientList];
  }

  removeClient(index: number): void {
    const item = this.selectedClientList[index];
    this.selectedClientList.splice(index, 1);
    if (item) item.is_active = 'N';
    this.cdr.detectChanges();
  }

  onClientListChange(): void {
    this.clientList.forEach((c) => {
      c.is_active = this.selectedClientList.includes(c) ? 'A' : 'N';
    });
    this.cdr.detectChanges();
  }

  // ----------------------------------------------------------------
  // Form line mapping (fromNewFormMapping)
  // ----------------------------------------------------------------

  private newFormLine(): FormLineMapping {
    return {
      form: '', schedule: '', line: '', column: '',
      section: '', part: '', form_chart_key: '', form_seq_key: '',
    };
  }

  addNewFormLineMapping(): void {
    this.formLineMapping.push(this.newFormLine());
    this.cdr.detectChanges();
  }

  onCreateFromChange(): void {
    if (this.createFrom === 'fromNewFormMapping' && this.formLineMapping.length === 0) {
      this.formLineMapping = [this.newFormLine()];
    }
    this.cdr.detectChanges();
  }

  // Cascade — replicates `getLookupNext` from CreateSytemAccountCtrl.js.

  onFormChange(idx: number): void {
    const row = this.formLineMapping[idx];
    row.schedule = ''; row.line = ''; row.column = ''; row.section = ''; row.part = '';

    const filtered = this.loadSystemAcctLookupTemp.filter((o: any) => o?.FORM == row.form);
    this.scheduleLookup[idx] = _.uniq(filtered.map((o: any) => o?.SCHEDULE));
    this.lineLookup[idx] = [];
    this.columnLookup[idx] = [];
    this.sectionLookup[idx] = [];
    this.partLookup[idx] = [];
    this.cdr.detectChanges();
  }

  onScheduleChange(idx: number): void {
    const row = this.formLineMapping[idx];
    row.line = ''; row.column = ''; row.section = ''; row.part = '';
    const filtered = this.loadSystemAcctLookupTemp.filter(
      (o: any) => o?.FORM == row.form && o?.SCHEDULE == row.schedule,
    );
    this.lineLookup[idx] = _.uniq(filtered.map((o: any) => o?.LINE));
    this.columnLookup[idx] = [];
    this.sectionLookup[idx] = [];
    this.partLookup[idx] = [];
    this.cdr.detectChanges();
  }

  onLineChange(idx: number): void {
    const row = this.formLineMapping[idx];
    row.column = ''; row.section = ''; row.part = '';
    const filtered = this.loadSystemAcctLookupTemp.filter(
      (o: any) => o?.FORM == row.form && o?.SCHEDULE == row.schedule && o?.LINE == row.line,
    );
    this.columnLookup[idx] = _.uniq(filtered.map((o: any) => o?.COL));
    this.sectionLookup[idx] = [];
    this.partLookup[idx] = [];
    this.cdr.detectChanges();
  }

  onColumnChange(idx: number): void {
    const row = this.formLineMapping[idx];
    row.section = ''; row.part = '';
    const filtered = this.loadSystemAcctLookupTemp.filter(
      (o: any) => o?.FORM == row.form && o?.SCHEDULE == row.schedule
        && o?.LINE == row.line && o?.COL == row.column,
    );
    this.sectionLookup[idx] = _.uniq(filtered.map((o: any) => o?.SECTION));
    this.partLookup[idx] = [];
    this.cdr.detectChanges();
  }

  onSectionChange(idx: number): void {
    const row = this.formLineMapping[idx];
    row.part = '';
    const filtered = this.loadSystemAcctLookupTemp.filter(
      (o: any) => o?.FORM == row.form && o?.SCHEDULE == row.schedule
        && o?.LINE == row.line && o?.COL == row.column && o?.SECTION == row.section,
    );
    this.partLookup[idx] = _.uniq(filtered.map((o: any) => o?.PART));
    this.cdr.detectChanges();
  }

  // ----------------------------------------------------------------
  // Account / desc input change handlers (kept to mirror UX of original)
  // ----------------------------------------------------------------

  get showAcctError(): boolean {
    return (this.submitted) && !this.newSysAccount?.toString().trim();
  }

  get showAcctDescError(): boolean {
    return (this.submitted) && !this.newSysAccountDesc?.toString().trim();
  }

  // ----------------------------------------------------------------
  // Validation, payload build, save
  // ----------------------------------------------------------------

  private validateData(): boolean {
    let haveError = false;
    const missing: string[] = [];

    if (!this.newSysAccount?.toString().trim()) haveError = true;
    if (!this.newSysAccountDesc?.toString().trim()) haveError = true;

    if (!this.acctType?.ACCT_TYPE) { haveError = true; missing.push('Account Type'); }
    if (!this.sourceSystem?.SOURCE_SYSTEM) { haveError = true; missing.push('Source System'); }
    if (this.selectedSysChart === null || this.selectedSysChart === undefined || this.selectedSysChart === '') {
      haveError = true;
      missing.push('System Chart');
    }

    if (missing.length > 0) {
      this.snackbar.openSnackBar('Please select ' + missing.join(', '), 'gtw-snackbar--red');
    }

    if (this.isClientSpecific && this.selectedClientList.length <= 0) {
      haveError = true;
      this.snackbar.openSnackBar('Please select at least 1 client', 'gtw-snackbar--red');
    }

    if (this.createFrom === 'fromExistingSysAcct') {
      if (!this.selectedFromSystemAcct) {
        haveError = true;
        this.snackbar.openSnackBar('Please select from system account', 'gtw-snackbar--red');
      }
    }

    if (this.createFrom === 'fromNewFormMapping') {
      for (const formLine of this.formLineMapping) {
        if (!formLine.form || !formLine.schedule || !formLine.column
            || !formLine.section || !formLine.part || !formLine.form_chart_key) {
          haveError = true;
          this.snackbar.openSnackBar('Please select all possible form mapping details', 'gtw-snackbar--red');
          break;
        }
      }
    }

    return haveError;
  }

  private buildPayload(): any {
    const sysAcctObj: any = {
      account: this.newSysAccount,
      account_desc: this.newSysAccountDesc,
      account_type: this.acctType?.ACCT_TYPE,
      source_sys: this.sourceSystem?.SOURCE_SYSTEM,
      is_client_specific: this.isClientSpecific ? true : false,
      sys_chart_key: this.selectedSysChart,
      starter_ds: this.starter_ds,
      fromSysAcctKey: this.createFrom === 'fromExistingSysAcct' ? this.selectedFromSystemAcct : '',
      form_data: this.formLineMapping,
    };

    const selectedClientsArray: string[] = [];
    if (!sysAcctObj.is_client_specific) {
      this.clientList.forEach((c) => {
        c.is_active = 'A';
        selectedClientsArray.push(c.label);
      });
    } else {
      this.clientList.forEach((c) => {
        if (c.is_active === 'A') {
          selectedClientsArray.push(c.label);
        }
      });
    }
    if (this.selectedClientList.length > 0) {
      this.selectedClientList.forEach((c) => {
        c.is_active = 'A';
        if (!selectedClientsArray.includes(c.label)) {
          selectedClientsArray.push(c.label);
        }
      });
    }

    sysAcctObj.selectedClients = this.clientList.map((client) =>
      this.stripDropdownMetadata(client),
    );
    sysAcctObj.selectedClientsString = selectedClientsArray.join(',');
    return sysAcctObj;
  }

  private stripDropdownMetadata(client: ClientItem): ClientItem {
    const data: any = { ...client };
    delete data.showCheckbox;
    delete data.role;
    return data as ClientItem;
  }

  save(): void {
    this.submitted = true;
    this.cdr.detectChanges();

    if (this.crudLoading) {
      this.snackbar.openSnackBar('Please wait while we save this request', 'gtw-snackbar--yellow');
      return;
    }

    if (this.validateData()) return;

    this.crudLoading = true;
    this.cdr.detectChanges();

    const payload = this.buildPayload();

    this.masterDataService
      .createSysAccount(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          this.crudLoading = false;
          // The legacy backend returns callSuccess as `1` (number).
          if (data?.callSuccess == 1 || data?.callSuccess === '1') {
            this.snackbar.openSnackBar('New system account created successfully', 'gtw-snackbar--green');
            this.dialogRef?.close('saved');
          } else {
            const failed = Array.isArray(data?.failedCients)
              ? data.failedCients.join(',')
              : (data?.failedCients || '');
            const msg = failed
              ? `System account creation failed for ${failed}. Select failed clients and try again after sometime`
              : (data?.errorMessage || 'Save failed');
            this.snackbar.openSnackBar(msg, 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.crudLoading = false;
          this.snackbar.openSnackBar('Error creating system account', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
  }

  reset(): void {
    this.submitted = false;
    this.newSysAccount = '';
    this.newSysAccountDesc = '';
    this.acctType = null;
    this.sourceSystem = null;
    this.selectedSysChart = null;
    this.createFrom = '';
    this.selectedFromSystemAcct = null;
    this.formLineMapping = [this.newFormLine()];
    this.scheduleLookup = [];
    this.lineLookup = [];
    this.columnLookup = [];
    this.sectionLookup = [];
    this.partLookup = [];
    this.isClientSpecific = false;
    this.selectedClientList = [];
    this.clientList.forEach((c) => (c.is_active = 'N'));
    this.cdr.detectChanges();
  }

  accountTypeItems: any[] = [];
  sourceSystemItems: any[] = [];
  sysChartItems: any[] = [];
  createFromItems: any[] = [];
  fromSystemAcctItems: any[] = [];
  formLookupItems: any[] = [];
  formChartItems: any[] = [];
  selectedAcctTypeItem: any = null;
  selectedSourceSystemItem: any = null;
  selectedSysChartItem: any = null;
  selectedCreateFromItem: any = null;
  selectedFromSystemAcctItem: any = null;
  rowDropdownItems: Record<string, any[][]> = {
    schedule: [],
    line: [],
    column: [],
    section: [],
    part: [],
  };

  private refreshTopDropdownItems(): void {
    this.accountTypeItems = mapToAppkitDropdownItems(this.accountTypes, 'ACCT_TYPE', 'ACCT_TYPE');
    this.sourceSystemItems = mapToAppkitDropdownItems(this.sourceSystemList, 'SOURCE_SYSTEM', 'SOURCE_SYSTEM');
    this.sysChartItems = mapToAppkitDropdownItems(this.sysChartList, 'SYS_CHART_CODE', 'SYS_CHART_KEY');
    this.createFromItems = mapToAppkitDropdownItems(this.createFromOptions, 'label', 'value');
    this.fromSystemAcctItems = mapToAppkitDropdownItems(this.sys_acc_dropdown_data, 'SYS_DROPDOWN', 'SYS_ACCT_KEY');
    this.formLookupItems = this.toPrimitiveDropdownItems(this.loadLookupForm);
    this.formChartItems = mapToAppkitDropdownItems(this.formChartList, 'FORM_CHART_CODE', 'FORM_CHART_KEY');
    this.selectedAcctTypeItem = this.accountTypeItems.find((i) => i.ACCT_TYPE === this.acctType?.ACCT_TYPE) || null;
    this.selectedSourceSystemItem = this.sourceSystemItems.find((i) => i.SOURCE_SYSTEM === this.sourceSystem?.SOURCE_SYSTEM) || null;
    this.selectedSysChartItem = findAppkitItemByValue(this.sysChartItems, this.selectedSysChart);
    this.selectedCreateFromItem = findAppkitItemByValue(this.createFromItems, this.createFrom);
    this.selectedFromSystemAcctItem = findAppkitItemByValue(this.fromSystemAcctItems, this.selectedFromSystemAcct);
  }

  private toPrimitiveDropdownItems(values: any[]): any[] {
    return (values || []).map((value, index) => ({
      value,
      label: String(value ?? '')
    })).filter((item) => item.label !== '');
  }

  getRowLookupItems(values: any[] | undefined): any[] {
    return this.toPrimitiveDropdownItems(values || []);
  }

  getRowSelectedItem(values: any[] | undefined, current: any): any {
    return findAppkitItemByValue(this.getRowLookupItems(values), current);
  }

  onAcctTypeSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedAcctTypeItem = selected;
    this.acctType = selected || null;
    this.cdr.detectChanges();
  }

  onSourceSystemSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedSourceSystemItem = selected;
    this.sourceSystem = selected || null;
    this.cdr.detectChanges();
  }

  onSysChartSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedSysChartItem = selected;
    this.selectedSysChart = selected?.value ?? null;
    this.cdr.detectChanges();
  }

  onCreateFromSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedCreateFromItem = selected;
    this.createFrom = (selected?.value ?? '') as any;
    this.onCreateFromChange();
  }

  onFromSystemAcctSelect(event: any): void {
    const selected = getAppkitDropdownSelection(event);
    this.selectedFromSystemAcctItem = selected;
    this.selectedFromSystemAcct = selected?.value ?? null;
    this.cdr.detectChanges();
  }

  onRowPrimitiveSelect(event: any, row: FormLineMapping, field: keyof FormLineMapping, idx?: number): void {
    const selected = getAppkitDropdownSelection(event);
    (row as any)[field] = selected?.value ?? '';
    if (field === 'form' && idx != null) {
      this.onFormChange(idx);
    } else if (field === 'schedule' && idx != null) {
      this.onScheduleChange(idx);
    } else if (field === 'line' && idx != null) {
      this.onLineChange(idx);
    } else if (field === 'column' && idx != null) {
      this.onColumnChange(idx);
    } else if (field === 'section' && idx != null) {
      this.onSectionChange(idx);
    }
    this.cdr.detectChanges();
  }

  onFormChartSelect(event: any, row: FormLineMapping): void {
    const selected = getAppkitDropdownSelection(event);
    row.form_chart_key = selected?.value ?? '';
    this.cdr.detectChanges();
  }

  onDropdownSelectItem(event: any, itemRef: { current: any }): void {
    const selected = getAppkitDropdownSelection(event);
    if (selected) {
      itemRef.current = selected;
    }
  }

  setDropdownAriaLabel(item: any, contextLabel: string = 'Option'): string {
    return setDropdownAriaLabel(item, contextLabel);
  }

  findDropdownItemByValue(items: any[], value: any): any {
    return findAppkitItemByValue(items, value);
  }

  cancel(): void {
    this.dialogRef?.close();
  }
}
